/**
 * ThemeProvider
 * Owns the landlord theme preference (System by default), follows the OS scheme while the
 * preference is 'system', and applies the resolved theme to <html>. Persistence is
 * device-local (localStorage) and best-effort: if storage fails the session still changes.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyTheme,
  readStoredPreference,
  resolveTheme,
  systemPrefersDark,
  writeStoredPreference,
  type ThemePreference,
} from '@/lib/theme';
import { ThemeContext, type ThemeContextValue } from './theme.context.js';

const DARK_QUERY = '(prefers-color-scheme: dark)';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    setSystemDark(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const systemTheme = systemDark ? 'dark' : 'light';
  const resolved = resolveTheme(preference, systemDark);

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    writeStoredPreference(p);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, systemTheme, setPreference }),
    [preference, resolved, systemTheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
